
import { supabase } from '@/integrations/supabase/client';
import { Blog, BlogContent } from '@/types/blog';

/**
 * Fetch all published blogs
 */
export const fetchPublishedBlogs = async (): Promise<Blog[]> => {
  try {
    const { data, error } = await supabase
      .from('blogs')
      .select(`
        id,
        title,
        slug,
        summary,
        content,
        cover_image,
        author_id,
        upvotes,
        created_at,
        updated_at,
        published,
        meta_description,
        meta_keywords
      `)
      .eq('published', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching blogs:', error);
      throw error;
    }

    // Fetch author names in a separate query
    const blogs = await Promise.all(
      data.map(async (blog) => {
        let authorName = 'Unknown';
        
        if (blog.author_id) {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('first_name, last_name')
            .eq('id', blog.author_id)
            .single();
          
          if (!userError && userData) {
            authorName = `${userData.first_name} ${userData.last_name}`;
          }
        }

        return {
          ...blog,
          id: blog.id,
          title: blog.title,
          slug: blog.slug,
          summary: blog.summary,
          content: blog.content as unknown as BlogContent[],
          coverImage: blog.cover_image,
          authorId: blog.author_id,
          authorName: authorName,
          upvotes: blog.upvotes,
          createdAt: blog.created_at,
          updatedAt: blog.updated_at,
          published: blog.published,
          metaDescription: blog.meta_description,
          metaKeywords: blog.meta_keywords
        };
      })
    );

    return blogs;
  } catch (error) {
    console.error('Exception fetching blogs:', error);
    throw error;
  }
};

/**
 * Fetch a single blog by slug
 */
export const fetchBlogBySlug = async (slug: string): Promise<Blog> => {
  try {
    const { data, error } = await supabase
      .from('blogs')
      .select(`
        id,
        title,
        slug,
        summary,
        content,
        cover_image,
        author_id,
        upvotes,
        created_at,
        updated_at,
        published,
        meta_description,
        meta_keywords
      `)
      .eq('slug', slug)
      .single();

    if (error) {
      console.error(`Error fetching blog with slug ${slug}:`, error);
      throw error;
    }

    // Fetch author name in a separate query
    let authorName = 'Unknown';
    
    if (data.author_id) {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('first_name, last_name')
        .eq('id', data.author_id)
        .single();
      
      if (!userError && userData) {
        authorName = `${userData.first_name} ${userData.last_name}`;
      }
    }

    return {
      ...data,
      id: data.id,
      title: data.title,
      slug: data.slug,
      summary: data.summary,
      content: data.content as unknown as BlogContent[],
      coverImage: data.cover_image,
      authorId: data.author_id,
      authorName: authorName,
      upvotes: data.upvotes,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      published: data.published,
      metaDescription: data.meta_description,
      metaKeywords: data.meta_keywords
    };
  } catch (error) {
    console.error(`Exception fetching blog with slug ${slug}:`, error);
    throw error;
  }
};

/**
 * Upvote a blog post
 */
export const upvoteBlog = async (blogId: string): Promise<boolean> => {
  try {
    // Get the client IP (this will be handled by the edge function)
    const ip = '0.0.0.0'; // Placeholder, the real IP will be determined server-side
    
    const { data, error } = await supabase.rpc('increment_blog_upvote', {
      blog_id_param: blogId,
      ip_address_param: ip
    });

    if (error) {
      console.error('Error upvoting blog:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Exception upvoting blog:', error);
    throw error;
  }
};

/**
 * Create a tracking edge function for upvoting
 */
export const trackBlogUpvote = async (blogId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.functions.invoke('track-blog-upvote', {
      body: { blogId }
    });

    if (error) {
      console.error('Error tracking blog upvote:', error);
      return false;
    }

    return data.success;
  } catch (error) {
    console.error('Exception tracking blog upvote:', error);
    return false;
  }
};
